import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, ShieldCheck, Loader2, Eye, EyeOff, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { NexusLogo } from '../components/brand/NexusLogo';
import { useT } from '../contexts/LocaleContext';
import { extractErrorMessage } from '../lib/errors';

export default function AuthPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get('token');
  const { login, register } = useAuth();
  const t = useT();
  const [mode, setMode] = useState<'landing' | 'email-in' | 'email-up' | 'forgot' | 'reset'>(
    resetToken ? 'reset' : 'landing'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [name, setName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState<'email' | 'forgot' | 'reset' | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy('email');
    try {
      if (mode === 'email-up') {
        await register(email.trim(), password, name.trim() || email.split('@')[0]);
      } else {
        await login(email.trim(), password);
      }
      // v1.11 · post-auth redirect goes to /home (main hub). Workspace
      // is created only when the user starts a project from Home.
      nav('/home', { replace: true });
    } catch (err: any) {
      toast.error(extractErrorMessage(err, t('auth.loginError') || t('auth.cannotContinue')));
    } finally {
      setBusy(null);
    }
  };

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy('forgot');
    try {
      const r = await apiService.forgotPassword(email.trim());
      if (r.reset_link) {
        setResetLink(r.reset_link);
        toast.success(t('auth.forgotToastGenerated'));
      } else {
        toast.success(r.message || t('auth.forgotToastMaybe'));
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, t('auth.forgotToastFail')));
    } finally {
      setBusy(null);
    }
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetToken) { toast.error(t('auth.resetInvalid')); return; }
    if (password.length < 8) { toast.error(t('auth.resetMin')); return; }
    if (password !== password2) { toast.error(t('auth.resetMismatch')); return; }
    setBusy('reset');
    try {
      await apiService.resetPassword(resetToken, password);
      toast.success(t('auth.resetOk'));
      setPassword(''); setPassword2('');
      setMode('email-in');
      nav('/auth', { replace: true });
    } catch (err: any) {
      toast.error(extractErrorMessage(err, t('auth.resetFail')));
    } finally {
      setBusy(null);
    }
  };

  // Emergent-managed Google Auth entry point.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main
      className="min-h-[100dvh] w-full flex flex-col items-center justify-between px-6 py-10 nx-circuit"
      style={{
        // Sprint v1.29 · #9 · Fix Login teclado móvil.
        // Cuando el teclado del móvil aparece, iOS/Android achican la
        // viewport dinámica (100dvh) y al cerrarlo dejan la página
        // desplazada. Fijamos scroll al top y evitamos overflow.
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        paddingTop: 'max(2.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
      }}
      onBlur={() => window.scrollTo(0, 0)}
      data-testid="auth-page"
    >
      {/* Hero block */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center gap-4 mb-10"
        >
          <NexusLogo size={96} glow />
          <div className="text-center">
            <div
              className="text-3xl font-light tracking-[0.35em]"
              style={{ color: 'hsl(var(--text))' }}
            >
              NEXUS
            </div>
            <div
              className="text-[11px] tracking-[0.35em] mt-1 uppercase"
              style={{ color: 'hsl(var(--text-muted))' }}
            >
              {t('brand.tagline')}
            </div>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="text-2xl font-medium mb-3 text-center"
          style={{ color: 'hsl(var(--text))' }}
        >
          {t('auth.welcome')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="text-sm text-center max-w-sm mb-10"
          style={{ color: 'hsl(var(--text-muted))' }}
        >
          {t('auth.subtitle')}
        </motion.p>

        <AnimatePresence mode="wait">
          {mode === 'landing' && (
            <motion.div
              key="landing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="w-full flex flex-col gap-3"
            >
              <button
                data-testid="auth-google-btn"
                onClick={startGoogleAuth}
                className="w-full flex items-center justify-center gap-3 rounded-full py-3 px-4 font-medium transition hover:shadow-lg"
                style={{
                  background: '#FFFFFF',
                  color: '#1F1F1F',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  fontFamily: '"Roboto", "Google Sans", -apple-system, "Segoe UI", sans-serif',
                  letterSpacing: '0.01em',
                }}
              >
                <svg className="w-[18px] h-[18px]" viewBox="0 0 48 48" aria-hidden xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                <span>{t('auth.continueWithGoogle')}</span>
              </button>

              <div className="flex items-center gap-3 my-1" aria-hidden>
                <div className="flex-1 h-px" style={{ background: 'hsl(var(--border))' }} />
                <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: 'hsl(var(--text-muted))' }}>o</span>
                <div className="flex-1 h-px" style={{ background: 'hsl(var(--border))' }} />
              </div>

              <button
                data-testid="auth-email-btn"
                onClick={() => setMode('email-in')}
                className="w-full flex items-center justify-between gap-3 rounded-2xl border py-3 px-4 hover:border-[hsl(var(--accent)/0.4)] transition"
                style={{
                  background: 'hsl(var(--bg-elev) / 0.7)',
                  borderColor: 'hsl(var(--border))',
                  color: 'hsl(var(--text))',
                }}
              >
                <span className="flex items-center gap-3">
                  <Mail className="w-4 h-4" style={{ color: 'hsl(var(--accent))' }} />
                  <span className="font-medium">{t('auth.continueWithEmail')}</span>
                </span>
                <ArrowRight className="w-4 h-4 opacity-60" />
              </button>

              <div className="text-center text-sm mt-4" style={{ color: 'hsl(var(--text-muted))' }}>
                {t('auth.noAccount')}{' '}
                <button
                  data-testid="auth-signup-toggle"
                  onClick={() => setMode('email-up')}
                  className="font-medium underline decoration-dotted underline-offset-4"
                  style={{ color: 'hsl(var(--accent))' }}
                >
                  {t('auth.createAccount')}
                </button>
              </div>
            </motion.div>
          )}

          {(mode === 'email-in' || mode === 'email-up') && (
            <motion.form
              key={mode}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              onSubmit={submitEmail}
              className="w-full flex flex-col gap-3"
            >
              {mode === 'email-up' && (
                <label className="relative block">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth.name')}
                    className="nx-input w-full"
                    style={{ paddingLeft: '2.75rem' }}
                    autoFocus
                  />
                </label>
              )}
              <label className="relative block">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email')}
                  autoComplete="email"
                  className="nx-input w-full"
                  style={{ paddingLeft: '2.75rem' }}
                  required
                />
              </label>
              <label className="relative block">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                <input
                  data-testid="auth-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password')}
                  autoComplete={mode === 'email-up' ? 'new-password' : 'current-password'}
                  className="nx-input w-full"
                  style={{ paddingLeft: '2.75rem', paddingRight: '2.75rem' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center"
                  style={{ color: 'hsl(var(--text-muted))' }}
                  aria-label={showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
                  data-testid="auth-password-toggle"
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </label>
              {mode === 'email-in' && (
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setResetLink(null); }}
                  className="self-end text-xs -mt-1"
                  style={{ color: 'hsl(var(--accent))' }}
                  data-testid="auth-forgot-link"
                >
                  {t('auth.forgotPassword')}
                </button>
              )}
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={busy === 'email'}
                className="nx-btn-primary justify-center w-full mt-1"
              >
                {busy === 'email' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {mode === 'email-up' ? t('auth.register') : t('auth.login')}
              </button>
              <button
                type="button"
                onClick={() => setMode('landing')}
                className="text-xs mt-1"
                style={{ color: 'hsl(var(--text-muted))' }}
              >
                ← {t('common.back')}
              </button>
              <div className="text-center text-sm mt-2" style={{ color: 'hsl(var(--text-muted))' }}>
                {mode === 'email-in' ? (
                  <>
                    {t('auth.noAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => setMode('email-up')}
                      className="underline decoration-dotted underline-offset-4"
                      style={{ color: 'hsl(var(--accent))' }}
                    >
                      {t('auth.createAccount')}
                    </button>
                  </>
                ) : (
                  <>
                    {t('auth.haveAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => setMode('email-in')}
                      className="underline decoration-dotted underline-offset-4"
                      style={{ color: 'hsl(var(--accent))' }}
                    >
                      {t('auth.login')}
                    </button>
                  </>
                )}
              </div>
            </motion.form>
          )}

          {mode === 'forgot' && (
            <motion.form
              key="forgot"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              onSubmit={submitForgot}
              className="w-full flex flex-col gap-3"
            >
              <div className="text-center mb-1">
                <div className="text-sm font-medium">{t('auth.recoverTitle')}</div>
                <div className="text-xs mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
                  {t('auth.recoverBody')}
                </div>
              </div>
              <label className="relative block">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  className="nx-input w-full"
                  style={{ paddingLeft: '2.75rem' }}
                  autoFocus
                  required
                />
              </label>
              <button
                type="submit"
                disabled={busy === 'forgot'}
                className="nx-btn-primary justify-center w-full"
                data-testid="forgot-submit-btn"
              >
                {busy === 'forgot' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {t('auth.sendRecoveryLink')}
              </button>
              {resetLink && (
                <div
                  className="rounded-xl p-3 text-xs space-y-2"
                  style={{ background: 'hsl(var(--accent) / 0.08)', border: '1px solid hsl(var(--accent) / 0.3)', color: 'hsl(var(--text))' }}
                  data-testid="forgot-link-panel"
                >
                  <div>{t('auth.linkGenerated')}</div>
                  <div className="font-mono break-all" style={{ color: 'hsl(var(--accent))' }}>{window.location.origin}{resetLink}</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(window.location.origin + resetLink); toast.success(t('auth.linkCopied')); }}
                      className="inline-flex items-center gap-1 text-[11px] font-medium"
                      style={{ color: 'hsl(var(--accent))' }}
                    >
                      <Copy size={11} /> {t('common.copy')}
                    </button>
                    <button
                      type="button"
                      onClick={() => window.location.assign(resetLink)}
                      className="text-[11px] font-medium"
                      style={{ color: 'hsl(var(--accent))' }}
                    >
                      {t('auth.openNow')}
                    </button>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => { setMode('email-in'); setResetLink(null); }}
                className="text-xs"
                style={{ color: 'hsl(var(--text-muted))' }}
              >
                ← {t('auth.backToLogin')}
              </button>
            </motion.form>
          )}

          {mode === 'reset' && (
            <motion.form
              key="reset"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              onSubmit={submitReset}
              className="w-full flex flex-col gap-3"
            >
              <div className="text-center mb-1">
                <div className="text-sm font-medium">{t('auth.newPasswordTitle')}</div>
                <div className="text-xs mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
                  {t('auth.newPasswordBody')}
                </div>
              </div>
              <label className="relative block">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                <input
                  data-testid="reset-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.newPasswordPlaceholder')}
                  className="nx-input w-full"
                  style={{ paddingLeft: '2.75rem', paddingRight: '2.75rem' }}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center"
                  style={{ color: 'hsl(var(--text-muted))' }}
                  aria-label={showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </label>
              <label className="relative block">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60 pointer-events-none" style={{ color: 'hsl(var(--text-muted))' }} />
                <input
                  data-testid="reset-password-confirm"
                  type={showPwd ? 'text' : 'password'}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  className="nx-input w-full"
                  style={{ paddingLeft: '2.75rem' }}
                  required
                />
              </label>
              <button
                type="submit"
                disabled={busy === 'reset'}
                className="nx-btn-primary justify-center w-full"
                data-testid="reset-submit-btn"
              >
                {busy === 'reset' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {t('auth.updatePassword')}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>

      <div
        className="flex items-center gap-6 text-xs uppercase tracking-[0.3em]"
        style={{ color: 'hsl(var(--text-muted))' }}
      >
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: 'hsl(var(--accent))' }} />
          {t('auth.secure')}
        </span>
        <span>·</span>
        <span>{t('auth.private')}</span>
        <span>·</span>
        <span>{t('auth.reliable')}</span>
      </div>
    </main>
  );
}
